import React, { useMemo, useState, useEffect } from "react";
import { colors } from "../../Styles";
import { EnhancedWidget, ProgressCircle, CheckBox, CheckStyles } from "..";
import { AnimalInfo, Graph_QL, Widget } from "../../Services";
import { random } from "lodash";
import { NewDefaultProgressCircles, NewDefaultProgressCircle } from "../_Components";

interface AutoReportWidgetProps {
  widget: Widget;
  animals: AnimalInfo[];
  connect: {
    connection: Graph_QL.Connection,
    domain: string
  }
}
/**
 * AutoReportWidget
 * @param header - The title displayed at the top of the widget.
 * @param systemId - The ID of the system to fetch related data.
 * @returns A React component displaying automated reports and progress circles within an enhanced widget.
 */
export default function ProgressWidget({ widget, animals, connect }: AutoReportWidgetProps) {
  const [dirtyWidget, setDirtyWidget] = useState(widget);

  useEffect(() => {
    setDirtyWidget(widget);
  },[widget])

  const resetWidget = () => {
    setDirtyWidget(widget);
  };

  // ProgressCircles
  const defaultProgressCircles = NewDefaultProgressCircles({ animals, connect } );

  const handleCheckBoxClick = (progressCircle: NewDefaultProgressCircle) => {
    if (dirtyWidget.data.includes(progressCircle.progressCircleId)) {
      setDirtyWidget({
        ...dirtyWidget,
        data: dirtyWidget.data.filter(id => id !== progressCircle.progressCircleId)
      });
    } else {
      setDirtyWidget({
        ...dirtyWidget,
        data: [...dirtyWidget.data, progressCircle.progressCircleId]
      });
    }
  }

  // #region | Left Content 
  const ProgressCirclesToShow = useMemo(() => {
    const activeData = widget.data;
  
    return defaultProgressCircles
      .filter(c => activeData.includes(c.progressCircleId))
      .map(progressCircle => (
        <div style={{ width: "160px" }}>
          <ProgressCircle
            key={progressCircle.progressCircleId}
            progressed={progressCircle.progressed}
            total={progressCircle.total}
            circleType={progressCircle.circleType}
            animation
            labels={{
              header: progressCircle.header,
              unit: progressCircle.unit,
            }}
            extension={{
              hasExtension: progressCircle.tooMuchFedDisclaimer
            }}
          />
        </div>
      ));
  }, [defaultProgressCircles, widget.data]);
  // #endregion

  return (
    <EnhancedWidget
      widget={dirtyWidget}
      changeData={setDirtyWidget}
      onCancel={resetWidget}
      overrides={{
        oneContentWidth: "fit-content"
      }}
      oneContent={
        <div style={{ display: "flex", height: "250px", boxSizing: "border-box", width: "100%", alignItems: "center", overflowX: "auto", scrollbarColor: colors.greyTertiary + colors.transparent, scrollbarWidth: "thin", padding: "20px" }}>
          {ProgressCirclesToShow}
        </div>
      }
      editMode={{
        editHeader: true,
        editOneContent: (
          <div style={{ height: "250px", display: "flex", flexDirection: "column", gap: "10px", boxSizing: "border-box", width: "100%", overflowY: "auto", scrollbarColor: colors.greyTertiary + colors.transparent, scrollbarWidth: "thin", padding: "20px" }}>
            {defaultProgressCircles.map((p) => (
              <CheckBox
                key={p.progressCircleId + random(0, 9999)}
                text={p.header}
                isChecked={(dirtyWidget.data).includes(p.progressCircleId)}
                checkStyle={CheckStyles.grey}
                onChange={() => handleCheckBoxClick(p)}
              />
            ))}
          </div>),
      }}
    />
  );
}